import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatusBadge,
  Tabs,
} from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { truncate } from "@/design-system/lib/format";
import {
  useCopyPromptsFromLegacy,
  usePromptCatalog,
  usePromptConfigurations,
  usePromptReconciliation,
} from "@/hooks/prompts/usePrompts";
import type {
  PromptCatalogEntry,
  PromptConfigurationRow,
  PromptReconciliation,
  PromptReconciliationItem,
} from "@/types/prompts";
import { PromptOverrideEditor } from "./PromptOverrideEditor";
import { promptKeyDisplayLabel } from "./promptKeyLabels";

const SOURCE_LABEL: Record<string, string> = {
  CollectionPrompt: "Collection",
  DealSubmissionPrompt: "Deal Submission",
  ReportTemplate: "Reports",
};

// Re-exported so existing importers keep one entry point.
export { promptKeyDisplayLabel };

export function PromptsAdminPage() {
  const toast = useToast();
  const [tab, setTab] = useState("store");
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const configurations = usePromptConfigurations();
  const catalog = usePromptCatalog();
  const reconciliation = usePromptReconciliation();
  const copy = useCopyPromptsFromLegacy();

  const rows = configurations.data ?? [];
  const catalogRows = catalog.data ?? [];
  const recon = reconciliation.data;

  const handleCopy = () => {
    void copy
      .mutateAsync()
      .then((r) =>
        toast.success(
          `Copied ${r.totalInserted} prompt(s) into the unified store (${r.totalSkipped} already present).`,
        ),
      )
      .catch((e) => toast.error(extractErrorMessage(e)));
  };

  const storeColumns: Column<PromptConfigurationRow>[] = [
    {
      key: "definitionKey",
      header: "Definition",
      render: (r) => (
        <span className="font-mono text-caption">{promptKeyDisplayLabel(r.definitionKey)}</span>
      ),
    },
    {
      key: "scope",
      header: "Scope",
      render: (r) =>
        r.isGlobal ? (
          <StatusBadge tone="info">Global override</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">{r.ownerType ?? "—"}</StatusBadge>
        ),
    },
    {
      key: "model",
      header: "Model",
      render: (r) => (
        <span className={r.model ? "" : "text-gray-mid"}>{r.model ?? "(catalog default)"}</span>
      ),
    },
    { key: "kind", header: "Kind", render: (r) => r.kind ?? r.outputLength ?? "—" },
    {
      key: "body",
      header: "Body",
      render: (r) =>
        r.hasBody ? (
          <span className="font-mono text-caption text-gray-mid">
            {truncate(r.bodyPreview ?? "", 60)} · {r.bodyLength} chars
          </span>
        ) : (
          <span className="text-gray-mid">—</span>
        ),
    },
    { key: "version", header: "Ver", render: (r) => r.version },
    {
      key: "active",
      header: "State",
      render: (r) =>
        r.isActive ? (
          <StatusBadge tone="ok">Active</StatusBadge>
        ) : (
          <StatusBadge tone="muted">Superseded</StatusBadge>
        ),
    },
    {
      key: "source",
      header: "Source",
      render: (r) => (r.sourceStore ? (SOURCE_LABEL[r.sourceStore] ?? r.sourceStore) : "native"),
    },
  ];

  const catalogColumns: Column<PromptCatalogEntry>[] = [
    {
      key: "key",
      header: "Call site",
      render: (r) => (
        <span className="font-mono text-caption">{promptKeyDisplayLabel(r.key)}</span>
      ),
    },
    { key: "service", header: "Service", render: (r) => r.service },
    { key: "model", header: "Model", render: (r) => r.model },
    { key: "temperature", header: "Temp", render: (r) => r.temperature },
    { key: "maxTokens", header: "Max tokens", render: (r) => r.maxTokens },
    {
      key: "bodySource",
      header: "Body source",
      render: (r) => <StatusBadge tone="neutral">{r.bodySource}</StatusBadge>,
    },
    {
      key: "override",
      header: "Override",
      render: (r) =>
        r.hasGlobalOverride ? (
          <StatusBadge tone="info">Overridden</StatusBadge>
        ) : (
          <span className="text-gray-mid">code default</span>
        ),
    },
    { key: "ownerBodies", header: "Owner bodies", render: (r) => r.ownerBodyCount },
    {
      key: "edit",
      header: "",
      render: (r) => (
        <Button
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            setEditingKey(r.key);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Admin"
        title="Prompts"
        description="Centralized AI prompt configuration. Open the Definitions tab and click a call site to override its model, sampling, token budget, caching, HTTP client — or, for the code-owned static prompts, its text. Overrides apply on the next call; leaving a field blank inherits the code default."
      />

      <div className="flex flex-wrap items-center gap-space-2">
        <Button onClick={handleCopy} loading={copy.isPending}>
          Copy from legacy
        </Button>
        <span className="text-caption text-gray-mid">
          Copies new prompt bodies from the legacy tables into the unified store. Copy-only and
          idempotent — never modifies the originals.
        </span>
      </div>

      <ReconciliationSummary recon={recon} loading={reconciliation.isLoading} />

      <Tabs
        tabs={[
          { id: "store", label: "Unified store" },
          { id: "definitions", label: "Definitions" },
          { id: "reconciliation", label: "Reconciliation" },
        ]}
        activeId={tab}
        onChange={setTab}
      >
        {tab === "store" && (
          <DataTable
            columns={storeColumns}
            rows={rows}
            rowKey={(r) => r.id}
            loading={configurations.isLoading}
            error={configurations.isError ? "Could not load the unified store." : null}
            emptyMessage="The unified store is empty. Click “Copy from legacy” to populate it."
          />
        )}
        {tab === "definitions" && (
          <DataTable
            columns={catalogColumns}
            rows={catalogRows}
            rowKey={(r) => r.key}
            loading={catalog.isLoading}
            error={catalog.isError ? "Could not load the catalog." : null}
            emptyMessage="No prompt definitions registered."
            onRowClick={(r) => setEditingKey(r.key)}
          />
        )}
        {tab === "reconciliation" && (
          <ReconciliationDetails recon={recon} loading={reconciliation.isLoading} />
        )}
      </Tabs>

      <PromptOverrideEditor promptKey={editingKey} onClose={() => setEditingKey(null)} />
    </div>
  );
}

function ReconciliationSummary({
  recon,
  loading,
}: {
  recon: PromptReconciliation | undefined;
  loading: boolean;
}) {
  if (loading || !recon) {
    return (
      <Card>
        <CardContent>
          <span className="text-gray-mid">Loading reconciliation…</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Legacy ↔ unified reconciliation{" "}
          {recon.inSync ? (
            <StatusBadge tone="ok">In sync</StatusBadge>
          ) : (
            <StatusBadge tone="warn">Out of sync</StatusBadge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-space-3">
          <Metric label="Legacy rows" value={recon.totalLegacy} />
          <Metric label="Copied" value={recon.totalCopied} />
          <Metric label="Missing" value={recon.totalMissing} tone={recon.totalMissing > 0 ? "warn" : "muted"} />
          <Metric label="Drifted" value={recon.totalDrifted} tone={recon.totalDrifted > 0 ? "warn" : "muted"} />
          <Metric label="Orphaned" value={recon.totalOrphaned} tone={recon.totalOrphaned > 0 ? "danger" : "muted"} />
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "warn" | "danger" | "muted";
}) {
  return (
    <div className="flex flex-col">
      <span className="text-caption text-gray-mid">{label}</span>
      {tone ? (
        <StatusBadge tone={tone}>{value}</StatusBadge>
      ) : (
        <span className="text-h4 font-semibold">{value}</span>
      )}
    </div>
  );
}

function ReconciliationDetails({
  recon,
  loading,
}: {
  recon: PromptReconciliation | undefined;
  loading: boolean;
}) {
  const itemColumns: Column<PromptReconciliationItem>[] = [
    {
      key: "status",
      header: "Status",
      render: (i) => <StatusBadge tone={statusTone(i.status)}>{i.status}</StatusBadge>,
    },
    {
      key: "definitionKey",
      header: "Definition",
      render: (i) => (
        <span className="font-mono text-caption">{promptKeyDisplayLabel(i.definitionKey)}</span>
      ),
    },
    { key: "detail", header: "Detail", render: (i) => i.detail },
    {
      key: "sourceRowId",
      header: "Source row",
      render: (i) => <span className="font-mono text-caption">{i.sourceRowId}</span>,
    },
  ];

  if (loading || !recon) {
    return <span className="text-gray-mid">Loading…</span>;
  }

  return (
    <div className="flex flex-col gap-space-4">
      {recon.stores.map((store) => (
        <Card key={store.sourceStore}>
          <CardHeader>
            <CardTitle>{SOURCE_LABEL[store.sourceStore] ?? store.sourceStore}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-space-3 flex flex-wrap gap-space-3">
              <Badge status="info">{store.legacyCount} legacy</Badge>
              <Badge status="finalized">{store.matchedCount} matched</Badge>
              {store.missingCount > 0 && <Badge status="review">{store.missingCount} missing</Badge>}
              {store.driftedCount > 0 && <Badge status="review">{store.driftedCount} drifted</Badge>}
              {store.orphanedCount > 0 && <Badge status="error">{store.orphanedCount} orphaned</Badge>}
            </div>
            {store.items.length === 0 ? (
              <span className="text-gray-mid">Fully in sync.</span>
            ) : (
              <>
                <DataTable
                  columns={itemColumns}
                  rows={store.items}
                  rowKey={(i) => `${i.status}-${i.sourceRowId}`}
                  emptyMessage="No differences."
                />
                {store.itemsTruncated && (
                  <p className="mt-space-2 text-caption text-gray-mid">
                    Showing the first {store.items.length} differences.
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function statusTone(status: string): "warn" | "danger" | "muted" {
  if (status === "Orphaned") return "danger";
  if (status === "Missing" || status === "Drifted") return "warn";
  return "muted";
}
